/**
 * GoogleMap Component
 *
 * Core map component that renders a Google Map and manages its lifecycle.
 * Children are cloned with google and map props for marker/overlay integration.
 */
import React, {
	Children,
	cloneElement,
	useState,
	useEffect,
	useRef,
	useCallback
} from "react";
import { debounce, isEqual } from "lodash";
import PropTypes from "prop-types";

import { createGoogleMap } from "../services/mapFactory";
import { getDistance, locationsToBounds } from "../services/geoUtils";

// Zoom in this many levels after fitBounds so facility pins appear more scattered (tighter view)
const BOUNDS_ZOOM_IN_LEVELS = 1;
const MAX_ZOOM = 20;

function GoogleMap({
	children,
	radius,
	center,
	locationBounds,
	onLoad,
	onMove,
	onMapReady,
	searchFromMove = false,
	setSearchFromMove,
	ariaLabel
}) {
	const [map, setMap] = useState(null);
	const [google, setGoogle] = useState(null);
	const [mapZoom, setMapZoom] = useState(null);
	const mapRef = useRef(null);
	const prevCenterRef = useRef(center);
	const onMoveRef = useRef(onMove);
	const centerRef = useRef(center);
	const mapZoomRef = useRef(null);
	const mapRefForHandler = useRef(null);
	const debouncedOnMapMoveRef = useRef(null);

	// Keep refs updated
	useEffect(() => {
		onMoveRef.current = onMove;
		centerRef.current = center;
		mapZoomRef.current = mapZoom;
		mapRefForHandler.current = map;
	}, [onMove, center, mapZoom, map]);

	// Set bounds function; zoom in a little so pins appear more scattered (tighter outbound)
	const setBounds = useCallback(() => {
		if (!google || !map || !center) return;

		const bounds = locationsToBounds(google, [
			center,
			...(locationBounds || [])
		]);

		// Don't zoom in too far on only one marker
		if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
			const extendPoint1 = new google.maps.LatLng(
				bounds.getNorthEast().lat() + 0.001,
				bounds.getNorthEast().lng() + 0.001
			);
			const extendPoint2 = new google.maps.LatLng(
				bounds.getNorthEast().lat() - 0.001,
				bounds.getNorthEast().lng() - 0.001
			);
			bounds.extend(extendPoint1);
			bounds.extend(extendPoint2);
		}

		map.fitBounds(bounds, 0);
		const zoom = map.getZoom();
		if (typeof zoom === "number" && zoom < MAX_ZOOM) {
			map.setZoom(Math.min(zoom + BOUNDS_ZOOM_IN_LEVELS, MAX_ZOOM));
		}
	}, [google, map, center, locationBounds]);

	// Initialize map on mount
	useEffect(() => {
		if (!mapRef.current) {
			return undefined;
		}

		const handleMapMove = () => {
			const currentMap = mapRefForHandler.current;
			if (!currentMap || !onMoveRef.current) return;

			const mapCenter = currentMap.getCenter();
			if (!mapCenter) return;
			const lat = mapCenter.lat();
			const lng = mapCenter.lng();
			const zoom = currentMap.getZoom();
			const prev = centerRef.current;
			const moved =
				!prev ||
				getDistance(prev.latitude, prev.longitude, lat, lng) > 0.1;
			const zoomChanged = mapZoomRef.current !== zoom;

			if (moved || zoomChanged) {
				onMoveRef.current({ latitude: lat, longitude: lng, zoom });
				setMapZoom(zoom);
				mapZoomRef.current = zoom;
			}
		};

		// Update zoom state only; do not call onMove so parent does not trigger search on zoom
		const handleZoomEnd = () => {
			const currentMap = mapRefForHandler.current;
			if (!currentMap) return;
			const zoom = currentMap.getZoom();
			setMapZoom(zoom);
			mapZoomRef.current = zoom;
		};

		debouncedOnMapMoveRef.current = debounce(handleMapMove, 500);

		const options = {
			radius: radius || 15,
			center,
			onDragEnd: debouncedOnMapMoveRef.current,
			onZoomEnd: handleZoomEnd
		};

		createGoogleMap(mapRef.current, options).then(
			({ google: googleInstance, map: mapInstance }) => {
				setGoogle(googleInstance);
				setMap(mapInstance);
				const initialZoom = mapInstance.getZoom();
				setMapZoom(initialZoom);
				mapZoomRef.current = initialZoom;
				mapRefForHandler.current = mapInstance;

				if (locationBounds) {
					// Use setTimeout to ensure map is fully initialized before setting bounds
					setTimeout(() => {
						const bounds = locationsToBounds(googleInstance, [
							center,
							...locationBounds
						]);
						if (
							bounds.getNorthEast().equals(bounds.getSouthWest())
						) {
							const extendPoint1 = new googleInstance.maps.LatLng(
								bounds.getNorthEast().lat() + 0.001,
								bounds.getNorthEast().lng() + 0.001
							);
							const extendPoint2 = new googleInstance.maps.LatLng(
								bounds.getNorthEast().lat() - 0.001,
								bounds.getNorthEast().lng() - 0.001
							);
							bounds.extend(extendPoint1);
							bounds.extend(extendPoint2);
						}
						mapInstance.fitBounds(bounds, 0);
						const zoom = mapInstance.getZoom();
						if (typeof zoom === "number" && zoom < MAX_ZOOM) {
							mapInstance.setZoom(
								Math.min(zoom + BOUNDS_ZOOM_IN_LEVELS, MAX_ZOOM)
							);
						}
					}, 0);
				}

				if (onLoad) {
					onLoad(true);
				}
				if (onMapReady) {
					onMapReady(mapInstance, googleInstance);
				}
			}
		);

		// Return cleanup function
		return () => {
			const debouncedFn = debouncedOnMapMoveRef.current;
			if (debouncedFn) {
				debouncedFn.cancel();
			}
		};
	}, []);

	// Update map center when center prop changes
	useEffect(() => {
		if (!map || !google || !center) {
			return;
		}

		// Only update if center actually changed
		if (isEqual(prevCenterRef.current, center)) {
			return;
		}

		const doNotMoveTheMap = searchFromMove;

		if (locationBounds && !doNotMoveTheMap) {
			setBounds();
		} else if (!doNotMoveTheMap) {
			map.setCenter(
				new google.maps.LatLng(center.latitude, center.longitude)
			);
		}

		if (searchFromMove && setSearchFromMove) {
			setSearchFromMove(false);
		}

		prevCenterRef.current = center;
	}, [
		center,
		map,
		google,
		locationBounds,
		searchFromMove,
		setSearchFromMove,
		setBounds
	]);

	return (
		<div
			className="google-map-container"
			role="application"
			aria-label={
				ariaLabel || "Interactive map displaying storage facilities"
			}
		>
			<div
				className="google-map"
				ref={mapRef}
			>
				{!!map &&
					Children.toArray(children).map((child) =>
						cloneElement(child, { google, map })
					)}
			</div>
		</div>
	);
}

GoogleMap.propTypes = {
	children: PropTypes.node,
	radius: PropTypes.number,
	center: PropTypes.shape({
		latitude: PropTypes.number,
		longitude: PropTypes.number
	}),
	locationBounds: PropTypes.arrayOf(
		PropTypes.shape({
			latitude: PropTypes.number,
			longitude: PropTypes.number
		})
	),
	onLoad: PropTypes.func,
	onMove: PropTypes.func,
	onMapReady: PropTypes.func,
	searchFromMove: PropTypes.bool,
	setSearchFromMove: PropTypes.func,
	ariaLabel: PropTypes.string
};

export default GoogleMap;
